import type { DocAttachment, DocDetail } from "@xop/dashboard-shared";

/**
 * What the single-document reader (`LibraryDocPage`) renders for a doc.
 *
 * The reader is **type-aware** — it never dumps a raw markdown body / web
 * scrape as plain text. The policy (reported 2026-06-09, "we must show ALL
 * documentations using the PDF reader … we should not show anything in plain
 * text", plus FAQ-as-Q&A and one-video-per-doc):
 *
 *   1. A playable VIDEO payload   → the video player (`VideoReader`).
 *   2. A parseable FAQ            → the Q&A viewer (`FaqReader`), one
 *                                   question + answer per item — never the
 *                                   canonical `## Q / A` markdown as text.
 *   3. A faithful ORIGINAL PDF    → the PDF reader, fed by the `source_file`
 *      (`source_file`)              passthrough. This wins over the
 *                                   transcribed markdown body, so an imported
 *                                   marketing/whitepaper page shows its real
 *                                   PDF, not the scraped page text (THE
 *                                   "we're showing the website itself" bug).
 *   4. An uploaded PDF            → the PDF reader, fed by the `/file`
 *                                   passthrough.
 *   5. An uploaded image / A-V    → the native `<img>` / `<video>` / `<audio>`
 *                                   (a picture or clip is not "plain text",
 *                                   and wrapping it in a generated PDF would be
 *                                   lossy) — see `FilePreview`.
 *   6. Any other uploaded binary  → a download card (Office / zip / … can't be
 *                                   previewed in the browser).
 *   7. A markdown BODY, no file   → the PDF reader, fed by a PDF **built
 *                                   client-side** from the body (`buildDocPdf`)
 *                                   — so a hand-authored article or a body-only
 *                                   imported page reads as a clean document,
 *                                   never a raw plain-text wall.
 *   8. Nothing at all             → an empty-state note.
 *
 * This selector is PURE (no React, no I/O) so the precedence is unit-tested in
 * `docReader.test.ts`; `LibraryDocPage` maps each mode to a renderer + (for the
 * PDF modes) the byte loader. Mirrors the customer portal's `Article.tsx`
 * dispatch (video → faq → fallback) so the two readers agree on which medium a
 * doc is.
 */
export type DocReaderMode =
  | { kind: "video" }
  | { kind: "faq" }
  /** Render through the inline PDF reader. `via` picks the byte source:
   * `source` = the `source_file` original PDF, `file` = the uploaded PDF
   * attachment, `built` = a PDF generated client-side from the markdown body. */
  | { kind: "pdf"; via: "source" | "file" | "built"; file: DocAttachment | null }
  | { kind: "image"; file: DocAttachment }
  | { kind: "av"; media: "video" | "audio"; file: DocAttachment }
  /** An uploaded TEXT file rendered inline — `.md`/`.markdown` through the
   * Markdown renderer (`markdown: true`), any other plain-text file (`.txt`,
   * `.csv`, `.json`, `.log`, …) as preformatted text. Bytes come through the
   * same-origin `/file` passthrough, so it's desktop-safe like the PDF modes. */
  | { kind: "text"; file: DocAttachment; markdown: boolean }
  | { kind: "file-card"; file: DocAttachment }
  | { kind: "empty" };

/** Lowercased file extension (no dot), or "" when there isn't one. */
function ext(filename: string): string {
  const m = filename.toLowerCase().match(/\.([a-z0-9]+)$/);
  return m?.[1] ?? "";
}

/** True when an attachment is a PDF (by MIME or `.pdf` extension). */
export function attachmentIsPdf(a: Pick<DocAttachment, "content_type" | "filename">): boolean {
  const ct = (a.content_type ?? "").toLowerCase();
  return ct === "application/pdf" || a.filename.toLowerCase().endsWith(".pdf");
}

const MARKDOWN_EXTS = ["md", "markdown", "mdown", "mkd", "mkdn"];
const TEXT_EXTS = [...MARKDOWN_EXTS, "txt", "text", "csv", "tsv", "log", "json", "yml", "yaml", "xml"];

/** True when an attachment is MARKDOWN (render through the Markdown renderer). */
export function attachmentIsMarkdown(a: Pick<DocAttachment, "content_type" | "filename">): boolean {
  const ct = (a.content_type ?? "").toLowerCase();
  return ct === "text/markdown" || ct === "text/x-markdown" || MARKDOWN_EXTS.includes(ext(a.filename));
}

/** True when an attachment is any plain-TEXT file the browser can decode +
 * render inline (markdown, txt, csv, json, …). Kept in step with the
 * `classifyFile` "text" class in `docPdf.ts` (the Download-PDF builder). */
export function attachmentIsText(a: Pick<DocAttachment, "content_type" | "filename">): boolean {
  const ct = (a.content_type ?? "").toLowerCase();
  return (
    ct.startsWith("text/") ||
    ct === "application/json" ||
    ct === "application/xml" ||
    TEXT_EXTS.includes(ext(a.filename))
  );
}

function hasBody(detail: Pick<DocDetail, "content">): boolean {
  return !!detail.content && detail.content.trim().length > 0;
}

export function selectDocReaderMode(detail: DocDetail): DocReaderMode {
  // 1. Playable video — route on the payload's presence (not doc_type), so a
  //    legacy article-typed doc whose video lives in `metadata.video_url`
  //    still plays. The serializer only emits `video` when it's playable.
  if (detail.video) return { kind: "video" };

  // 2. Parseable FAQ.
  if (detail.doc_type === "faq" && (detail.faq_items?.length ?? 0) > 0) {
    return { kind: "faq" };
  }

  // 3. Faithful original PDF wins over the transcribed body.
  const source = detail.source_file ?? null;
  if (source && attachmentIsPdf(source)) {
    return { kind: "pdf", via: "source", file: source };
  }

  // 4–6. Uploaded file (upload-mode docs have an empty body, so this and the
  //      body branch below are mutually exclusive in practice).
  const file = detail.files[0] ?? null;
  if (file) {
    if (attachmentIsPdf(file)) return { kind: "pdf", via: "file", file };
    const ct = (file.content_type ?? "").toLowerCase();
    if (ct.startsWith("image/")) return { kind: "image", file };
    if (ct.startsWith("video/")) return { kind: "av", media: "video", file };
    if (ct.startsWith("audio/")) return { kind: "av", media: "audio", file };
    // A text/markdown upload renders inline (Markdown for `.md`, preformatted
    // for other text) instead of falling through to the download-only card.
    if (attachmentIsText(file)) return { kind: "text", file, markdown: attachmentIsMarkdown(file) };
    return { kind: "file-card", file };
  }

  // 7. Markdown body, no file → render as a client-built PDF.
  if (hasBody(detail)) return { kind: "pdf", via: "built", file: null };

  // 8. Nothing.
  return { kind: "empty" };
}
